import {
    confirmGiftPayment,
    fetchGiftPaymentOrder,
    PaymentApiError,
} from './api';
import { ConfirmPaymentResponse, PaymentOrderStatus, PaymentOrderView } from '../types';

const CONFIRM_ATTEMPTS = 3;
const ORDER_POLL_ATTEMPTS = 3;
const INTERMEDIATE_STATUSES: PaymentOrderStatus[] = [
    'PAYMENT_PROCESSING',
    'PAID',
    'ISSUING',
    'MANUAL_RECONCILIATION_REQUIRED',
];
const FAILURE_STATUSES: PaymentOrderStatus[] = [
    'PAYMENT_FAILED',
    'ISSUE_FAILED',
    'PAYMENT_REVERSED',
    'EXPIRED',
];

type Delay = (milliseconds: number) => Promise<void>;

interface CompletionDependencies {
    fetchOrder: typeof fetchGiftPaymentOrder;
    confirmPayment: typeof confirmGiftPayment;
    delay: Delay;
}

export type PaymentCompletionResult =
    | { kind: 'success'; order: PaymentOrderView }
    | { kind: 'processing'; status: PaymentOrderStatus };

const defaultDelay: Delay = (milliseconds) =>
    new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
    });

const defaultDependencies: CompletionDependencies = {
    fetchOrder: fetchGiftPaymentOrder,
    confirmPayment: confirmGiftPayment,
    delay: defaultDelay,
};

const confirmWithRetry = async (
    orderId: string,
    paymentKey: string,
    amount: number,
    csrfToken: string,
    dependencies: CompletionDependencies,
): Promise<ConfirmPaymentResponse> => {
    for (let attempt = 1; attempt <= CONFIRM_ATTEMPTS; attempt += 1) {
        try {
            return await dependencies.confirmPayment(orderId, paymentKey, amount, csrfToken);
        } catch (error) {
            const canRetry =
                error instanceof PaymentApiError &&
                error.errorCode === 'PAYMENT_CONFIRMATION_UNKNOWN' &&
                attempt < CONFIRM_ATTEMPTS;
            if (!canRetry) throw error;
            await dependencies.delay(attempt * 400);
        }
    }

    throw new Error('결제 승인 결과를 확인하지 못했어요.');
};

const statusError = (status: PaymentOrderStatus): Error =>
    new Error(`선물 처리를 완료하지 못했어요. 현재 상태: ${status}`);

export const completeGiftPaymentFlow = async (
    orderId: string,
    paymentKey: string,
    amount: number,
    csrfToken: string | null,
    dependencies: CompletionDependencies = defaultDependencies,
): Promise<PaymentCompletionResult> => {
    let order = await dependencies.fetchOrder(orderId);
    if (order.orderId !== orderId) {
        throw new Error('조회한 주문 정보가 일치하지 않아요.');
    }
    if (order.status === 'SENT') {
        return { kind: 'success', order };
    }
    if (FAILURE_STATUSES.includes(order.status)) {
        throw statusError(order.status);
    }
    if (amount !== order.amounts.final) {
        throw new Error('결제 금액과 주문 금액이 일치하지 않아요.');
    }
    if (!csrfToken) {
        throw new Error('결제 세션이 만료되었어요. 앱에서 다시 시도해 주세요.');
    }

    const confirmed = await confirmWithRetry(
        orderId,
        paymentKey,
        amount,
        csrfToken,
        dependencies,
    );
    if (confirmed.orderId !== orderId) {
        throw new Error('승인된 주문 정보가 일치하지 않아요.');
    }
    if (confirmed.status === 'SENT') {
        return { kind: 'success', order };
    }
    if (FAILURE_STATUSES.includes(confirmed.status)) {
        throw statusError(confirmed.status);
    }
    if (!INTERMEDIATE_STATUSES.includes(confirmed.status)) {
        return { kind: 'processing', status: confirmed.status };
    }

    for (let attempt = 1; attempt <= ORDER_POLL_ATTEMPTS; attempt += 1) {
        await dependencies.delay(attempt * 500);
        order = await dependencies.fetchOrder(orderId);
        if (order.orderId !== orderId) {
            throw new Error('조회한 주문 정보가 일치하지 않아요.');
        }
        if (order.status === 'SENT') {
            return { kind: 'success', order };
        }
        if (FAILURE_STATUSES.includes(order.status)) {
            throw statusError(order.status);
        }
        if (!INTERMEDIATE_STATUSES.includes(order.status)) {
            return { kind: 'processing', status: order.status };
        }
    }

    return { kind: 'processing', status: order.status };
};

const inFlightCompletions = new Map<string, Promise<PaymentCompletionResult>>();

/**
 * React StrictMode mounts effects twice in development. Share only active work;
 * settled requests are removed so a later page visit can poll again.
 */
export const getOrCreatePaymentCompletion = (
    orderId: string,
    paymentKey: string,
    amount: number,
    csrfToken: string | null,
): Promise<PaymentCompletionResult> => {
    const key = `${orderId}:${paymentKey}:${amount}`;
    const existing = inFlightCompletions.get(key);
    if (existing) return existing;

    const request = completeGiftPaymentFlow(orderId, paymentKey, amount, csrfToken).finally(() => {
        inFlightCompletions.delete(key);
    });
    inFlightCompletions.set(key, request);
    return request;
};
